import { DRILL_FINDING_TYPES, type DrillFindingSuggestion } from '@felix/domain';
import {
  Button,
  Checkbox,
  Modal,
  Radio,
  Select,
  Stack,
  Textarea,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { hsePath } from '../hse/routes.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import work from '../pms/PmsWork.module.css';
import classes from './DrillRecords.module.css';
import type {
  DrillFinding,
  DrillFindingInput,
  DrillFindingLink,
  DrillFindingStatus,
  DrillFindingType,
  DrillSourceRef,
} from './finding-types.js';
import type { DrillRecordSource } from './record-source.js';
import type { DrillRecordDetail } from './record-types.js';

const STATUS_TONE: Record<DrillFindingStatus, StatusTone> = {
  open: 'warning',
  in_progress: 'info',
  closed: 'success',
  cancelled: 'neutral',
};

/** Bulgunun durum rozeti (gecikmişse ayrıca "Overdue"). */
export function DrillFindingStatusBadge({ finding }: { finding: DrillFinding }) {
  const { t } = useTranslation();
  return (
    <span className={classes.badges}>
      <StatusBadge tone={STATUS_TONE[finding.status]}>
        {t(`drillFinding.status.${finding.status}`)}
      </StatusBadge>
      {finding.overdue ? (
        <StatusBadge tone="danger">{t('drillFinding.overdue')}</StatusBadge>
      ) : null}
    </span>
  );
}

/** Bağlı kaydın yolu (kendi modülünde açılır). */
export function drillLinkPath(link: DrillFindingLink, vesselId: string, office: boolean): string {
  const vessel = office ? `&vessel=${vesselId}` : '';
  switch (link.kind) {
    case 'defect':
      return `/pms/defects?id=${link.id}`;
    case 'hse_report':
      return hsePath('non_conformity', { view: 'detail', id: link.id, tab: null });
    case 'work_order':
      return `/pms/work-orders?wo=${link.id}${vessel}`;
    case 'training_need':
      return `/crew/training?need=${link.id}${vessel}`;
  }
}

/** Kaynak tatbikat kaydının yolu ("Source: Drill DR-…"). */
export function drillRecordPath(recordId: string, vesselId?: string | null): string {
  const params = new URLSearchParams({ record: recordId, stage: 'close' });
  if (vesselId) params.set('vessel', vesselId);
  return `/operations/drills/records?${params.toString()}`;
}

/**
 * Bağlı kayıtta kaynak tatbikat ("Source: Drill DR-…"; belge §7 izlenebilirlik): arıza, uygunsuzluk,
 * iş emri ve eğitim ihtiyacından tatbikat kaydının Close aşamasına döner.
 */
export function DrillSourceLink({
  source,
  vesselId,
}: {
  source: DrillSourceRef | null | undefined;
  vesselId?: string | null;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  if (!source) return null;
  return (
    <span data-testid="drill-source">
      <button
        type="button"
        className={classes.issueLink}
        onClick={() => shell?.navigate(drillRecordPath(source.recordId, vesselId ?? null))}
      >
        {t('drillFinding.source', { no: source.recordNo })}
      </button>{' '}
      <span className={classes.muted}>
        ({t('drillFinding.sourceFinding', { no: source.findingNo })})
      </span>
    </span>
  );
}

/** Bağlı kayıt hücresi: numara ve durum; arızada zincir (iş emri, talep). */
export function DrillFindingLinkCell({
  finding,
  office,
}: {
  finding: DrillFinding;
  office: boolean;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const link = finding.link;
  if (!link) return <span className={classes.muted}>—</span>;
  const go = (path: string) => shell?.navigate(path);
  return (
    <Stack gap={2}>
      <span>
        <button
          type="button"
          className={classes.issueLink}
          onClick={() => go(drillLinkPath(link, finding.vesselId, office))}
        >
          {link.no}
        </button>{' '}
        <span className={classes.muted}>· {link.status.replaceAll('_', ' ')}</span>
      </span>
      {link.workOrders.length > 0 ? (
        <span className={classes.muted}>
          {t('drillFinding.workOrders')}:{' '}
          {link.workOrders.map((wo, index) => (
            <span key={wo.id}>
              {index > 0 ? ', ' : ''}
              <button
                type="button"
                className={classes.issueLink}
                onClick={() =>
                  go(`/pms/work-orders?wo=${wo.id}${office ? `&vessel=${finding.vesselId}` : ''}`)
                }
              >
                {wo.woNo}
              </button>
            </span>
          ))}
        </span>
      ) : null}
      {link.requisitions.length > 0 ? (
        <span className={classes.muted}>
          {t('drillFinding.requisitions')}:{' '}
          {link.requisitions.map((r, index) => (
            <span key={r.id}>
              {index > 0 ? ', ' : ''}
              <button
                type="button"
                className={classes.issueLink}
                onClick={() => go(`/purchasing/requisitions?id=${r.id}`)}
              >
                {r.requisitionNo}
              </button>
            </span>
          ))}
        </span>
      ) : null}
    </Stack>
  );
}

function useFindingErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    return t(`drillFinding.errors.${code}`, {
      defaultValue: t(`drillRecord.errors.${code}`, {
        defaultValue: t(`drillPlan.errors.${code}`, {
          defaultValue: t('drillPlan.errors.GENERIC'),
        }),
      }),
    });
  };
}

function Failure({ error }: { error: unknown }) {
  const text = useFindingErrorText();
  if (!error) return null;
  return (
    <p className={work.error} role="alert">
      {text(error)}
    </p>
  );
}

type Dialog =
  | { kind: 'form'; finding: DrillFinding | null; preset: Partial<DrillFindingInput> | null }
  | { kind: 'cancel'; finding: DrillFinding }
  | { kind: 'open'; finding: DrillFinding }
  | { kind: 'review'; finding: DrillFinding }
  | null;

/**
 * Close ▸ Findings & Corrective Actions (belge §7; ADR 0128 Faz 4 Ek): öneriler (siz seçmeden
 * açılmaz), bulgu tablosu, bağlı kaydı açma, iptal ve ofis değerlendirmesi. Uygunsuzluk yalnız
 * kaptanın sınıflamasıyla (`can.classify`); açık bulgu onayı engellemez.
 */
export function DrillFindingsPanel({
  record,
  source,
  reload,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
  reload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) => t(`drillFinding.${key}`, values);
  const [dialog, setDialog] = useState<Dialog>(null);
  const findings = record.findings ?? [];
  const suggestions = record.suggestions ?? [];
  const ship = record.side === 'ship';
  const office = record.side === 'office';
  const writable = ship && record.state !== 'cancelled';
  const canRaise =
    writable && (record.can.create || record.can.edit) && Boolean(source.raiseFinding);
  const canEdit = writable && record.can.edit;
  const canReview = office && Boolean(record.can.review) && Boolean(source.reviewFinding);
  const open =
    record.openFindings ??
    findings.filter((x) => x.status === 'open' || x.status === 'in_progress').length;
  const done = async () => {
    setDialog(null);
    await reload();
  };
  const presetOf = (s: DrillFindingSuggestion): Partial<DrillFindingInput> => ({
    ...(s.type ? { type: s.type } : {}),
    source: s.source,
    sourceKey: s.sourceKey,
    description: s.description,
    personUserId: s.personUserId,
  });
  return (
    <section aria-labelledby="drill-findings-title" data-testid="drill-findings">
      <div className={classes.findingsHead}>
        <h4 id="drill-findings-title" className={classes.sectionTitle}>
          {f('title')}
        </h4>
        <span className={classes.badges}>
          {open > 0 ? (
            <StatusBadge tone="warning">{f('openCount', { count: open })}</StatusBadge>
          ) : null}
          {canRaise ? (
            <Button
              size="sm"
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => setDialog({ kind: 'form', finding: null, preset: null })}
            >
              {f('actions.add')}
            </Button>
          ) : null}
        </span>
      </div>
      <p className={classes.hint}>{f('hint')}</p>
      {suggestions.length > 0 && record.state !== 'cancelled' ? (
        <div className={classes.suggestions}>
          <strong className={classes.noticeTitle}>
            {f('unraised', { count: suggestions.length })}
          </strong>
          <p className={classes.hint}>{f('suggestionsHint')}</p>
          <ul className={classes.checklist}>
            {suggestions.map((s) => (
              <li key={`${s.source}-${s.sourceKey}`} className={classes.suggestion}>
                <span>
                  <span className={classes.muted}>{f(`sources.${s.source}`)} · </span>
                  {s.description}
                  {s.type ? <span className={classes.muted}> → {f(`types.${s.type}`)}</span> : null}
                </span>
                {canRaise ? (
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => setDialog({ kind: 'form', finding: null, preset: presetOf(s) })}
                  >
                    {f('actions.raise')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {findings.length === 0 ? (
        <p className={classes.muted}>{f('empty')}</p>
      ) : (
        <div className={table.tableBox}>
          <table className={table.table} data-cards>
            <thead>
              <tr>
                <th scope="col">{f('columns.no')}</th>
                <th scope="col">{f('columns.type')}</th>
                <th scope="col">{f('columns.description')}</th>
                <th scope="col">{f('columns.responsible')}</th>
                <th scope="col">{f('columns.due')}</th>
                <th scope="col">{f('columns.linked')}</th>
                <th scope="col">{f('columns.status')}</th>
                <th scope="col">
                  <VisuallyHidden>{f('columns.actions')}</VisuallyHidden>
                </th>
              </tr>
            </thead>
            <tbody>
              {findings.map((finding) => {
                const active = finding.status !== 'cancelled';
                const canOpen =
                  canEdit &&
                  active &&
                  !finding.link &&
                  Boolean(source.openFinding) &&
                  (finding.type !== 'sms_nc' || Boolean(record.can.classify));
                return (
                  <tr key={finding.id}>
                    <td data-label={f('columns.no')}>
                      <strong>{finding.findingNo}</strong>
                    </td>
                    <td data-label={f('columns.type')}>{f(`types.${finding.type}`)}</td>
                    <td data-label={f('columns.description')}>
                      <Stack gap={2}>
                        <span>{finding.description}</span>
                        {finding.personName ? (
                          <span className={classes.muted}>
                            {f('columns.person')}: {finding.personName}
                          </span>
                        ) : null}
                        {finding.cancelReason ? (
                          <span className={classes.muted}>{finding.cancelReason}</span>
                        ) : null}
                        {finding.reviews.map((r) => (
                          <span key={r.id} className={classes.muted}>
                            {f('review.reviewed')}: {f(`review.${r.assessment}`)} ·{' '}
                            {r.reviewedByName ?? '—'} · {formatDateTime(r.reviewedAt)}
                            {r.comment ? ` — ${r.comment}` : ''}
                          </span>
                        ))}
                      </Stack>
                    </td>
                    <td data-label={f('columns.responsible')}>{finding.responsibleName ?? '—'}</td>
                    <td data-label={f('columns.due')}>
                      {finding.dueOn ? formatDay(finding.dueOn) : '—'}
                    </td>
                    <td data-label={f('columns.linked')}>
                      <DrillFindingLinkCell finding={finding} office={office} />
                    </td>
                    <td data-label={f('columns.status')}>
                      <DrillFindingStatusBadge finding={finding} />
                    </td>
                    <td>
                      <span className={classes.rowActions}>
                        {canOpen ? (
                          <Button size="xs" onClick={() => setDialog({ kind: 'open', finding })}>
                            {f(`actions.open.${finding.type}`)}
                          </Button>
                        ) : null}
                        {canEdit && active && source.updateFinding ? (
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setDialog({ kind: 'form', finding, preset: null })}
                          >
                            {f('actions.edit')}
                          </Button>
                        ) : null}
                        {canEdit && active && !finding.link && source.cancelFinding ? (
                          <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={() => setDialog({ kind: 'cancel', finding })}
                          >
                            {f('actions.cancel')}
                          </Button>
                        ) : null}
                        {canReview && active ? (
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setDialog({ kind: 'review', finding })}
                          >
                            {f('actions.review')}
                          </Button>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={classes.hint}>{f('approveNote')}</p>
      {dialog?.kind === 'form' ? (
        <FindingDialog
          record={record}
          source={source}
          finding={dialog.finding}
          preset={dialog.preset}
          onClose={() => setDialog(null)}
          onSaved={done}
        />
      ) : null}
      {dialog?.kind === 'cancel' ? (
        <CancelFindingDialog
          finding={dialog.finding}
          source={source}
          onClose={() => setDialog(null)}
          onSaved={done}
        />
      ) : null}
      {dialog?.kind === 'open' ? (
        <OpenRecordDialog
          record={record}
          finding={dialog.finding}
          source={source}
          onClose={() => setDialog(null)}
          onSaved={done}
        />
      ) : null}
      {dialog?.kind === 'review' ? (
        <ReviewDialog
          finding={dialog.finding}
          source={source}
          onClose={() => setDialog(null)}
          onSaved={done}
        />
      ) : null}
    </section>
  );
}

/** Ortak çalışma: kaydeder, hatayı gösterir. */
function useRun(onSaved: () => Promise<void> | void) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const run = async (work: () => Promise<boolean>) => {
    setBusy(true);
    setFailure(null);
    try {
      if (await work()) await onSaved();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };
  return { busy, failure, run };
}

function FindingDialog({
  record,
  source,
  finding,
  preset,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
  finding: DrillFinding | null;
  preset: Partial<DrillFindingInput> | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.form.${key}`, values);
  const [type, setType] = useState<DrillFindingType | null>(finding?.type ?? preset?.type ?? null);
  const [description, setDescription] = useState(finding?.description ?? preset?.description ?? '');
  const [responsible, setResponsible] = useState<string | null>(
    finding?.responsibleUserId ?? record.general.responsibleUserId ?? null,
  );
  const [dueOn, setDueOn] = useState<string | null>(finding?.dueOn ?? null);
  const [redrill, setRedrill] = useState(finding?.redrill ?? false);
  const [person, setPerson] = useState<string | null>(
    finding?.personUserId ?? preset?.personUserId ?? null,
  );
  const [missing, setMissing] = useState(false);
  const { busy, failure, run } = useRun(onSaved);
  const linked = Boolean(finding?.link);
  const types = DRILL_FINDING_TYPES.filter(
    (x) => x !== 'sms_nc' || record.can.classify || finding?.type === 'sms_nc',
  );
  const present = record.participants.filter((p) => p.attendance === 'present' && p.userId);
  const crew = record.crewOnBoard.map((c) => ({
    value: c.userId,
    label: c.rank ? `${c.name} · ${c.rank}` : c.name,
  }));
  const save = () =>
    run(async () => {
      if (!type || description.trim() === '' || (type === 'training_need' && !person)) {
        setMissing(true);
        return false;
      }
      const input: DrillFindingInput = {
        type,
        source: finding?.source ?? preset?.source ?? 'manual',
        sourceKey: finding?.sourceKey ?? preset?.sourceKey ?? null,
        description: description.trim(),
        responsibleUserId: responsible,
        dueOn,
        redrill,
        personUserId: type === 'training_need' ? person : null,
      };
      if (finding) {
        await source.updateFinding?.({
          id: finding.id,
          rowVersion: finding.rowVersion,
          finding: input,
        });
      } else {
        await source.raiseFinding?.({ recordId: record.id, finding: input });
      }
      return true;
    });
  return (
    <Modal
      opened
      onClose={onClose}
      title={finding ? f('titleEdit', { no: finding.findingNo }) : f('titleNew')}
      closeButtonProps={{ 'aria-label': f('close') }}
      size="lg"
    >
      <Stack gap="sm">
        {linked ? <p className={classes.hint}>{f('linkedNote')}</p> : null}
        <Radio.Group
          label={f('type')}
          required
          value={type ?? ''}
          onChange={(value) => setType(value as DrillFindingType)}
          error={missing && !type ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        >
          <Stack gap={6} mt={6}>
            {types.map((x) => (
              <Radio
                key={x}
                value={x}
                label={t(`drillFinding.types.${x}`)}
                disabled={linked && x !== finding?.type}
              />
            ))}
          </Stack>
        </Radio.Group>
        {!record.can.classify ? <p className={classes.hint}>{f('ncNote')}</p> : null}
        <Textarea
          label={f('description')}
          required
          autosize
          minRows={2}
          maxLength={2000}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          error={
            missing && description.trim() === '' ? t('drillPlan.fieldErrors.REQUIRED') : undefined
          }
        />
        {type === 'training_need' ? (
          <Select
            label={f('person')}
            required
            disabled={linked}
            data={present.map((p) => ({
              value: String(p.userId),
              label: p.rank ? `${p.name} · ${p.rank}` : p.name,
            }))}
            value={person}
            onChange={setPerson}
            error={missing && !person ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
          />
        ) : null}
        <div className={classes.grid2}>
          <Select
            label={f('responsible')}
            searchable
            clearable
            data={crew}
            value={responsible}
            onChange={setResponsible}
          />
          <DayInput label={f('due')} value={dueOn} onChange={(value) => setDueOn(value ?? null)} />
        </div>
        <Checkbox
          label={f('redrill')}
          description={f('redrillHint')}
          checked={redrill}
          onChange={(event) => setRedrill(event.currentTarget.checked)}
        />
        <Failure error={failure} />
        <div className={classes.footerGroup}>
          <Button loading={busy} onClick={() => void save()}>
            {f('save')}
          </Button>
          <Button variant="default" onClick={onClose}>
            {f('close')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

function CancelFindingDialog({
  finding,
  source,
  onClose,
  onSaved,
}: {
  finding: DrillFinding;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.cancelDialog.${key}`, values);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const { busy, failure, run } = useRun(onSaved);
  return (
    <Modal
      opened
      onClose={onClose}
      title={f('title', { no: finding.findingNo })}
      closeButtonProps={{ 'aria-label': t('drillFinding.form.close') }}
    >
      <Stack gap="sm">
        <Textarea
          label={f('reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <Failure error={failure} />
        <div className={classes.footerGroup}>
          <Button
            color="red"
            loading={busy}
            onClick={() =>
              void run(async () => {
                if (reason.trim() === '') {
                  setMissing(true);
                  return false;
                }
                await source.cancelFinding?.({
                  id: finding.id,
                  rowVersion: finding.rowVersion,
                  reason: reason.trim(),
                });
                return true;
              })
            }
          >
            {f('confirm')}
          </Button>
          <Button variant="default" onClick={onClose}>
            {t('drillFinding.form.close')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/**
 * Bağlı kaydı açma: arızada ekipman ya da sistem adı ve ciddiyet (Part A), iş emrinde ekipman ve
 * hedef gün; uygunsuzlukta HSEQ taslağı; eğitim ihtiyacında kişi bulgudan.
 */
function OpenRecordDialog({
  record,
  finding,
  source,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail;
  finding: DrillFinding;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.openDialog.${key}`, values);
  const equipment = (record.equipment ?? []).map((e) => ({
    value: e.id,
    label: `${e.code} · ${e.name}`,
  }));
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const [systemName, setSystemName] = useState('');
  const [severity, setSeverity] = useState<'minor' | 'major' | 'critical'>('minor');
  const [targetOn, setTargetOn] = useState<string | null>(finding.dueOn);
  const [missing, setMissing] = useState(false);
  const { busy, failure, run } = useRun(onSaved);
  const type = finding.type;
  const confirm = () =>
    run(async () => {
      if (type === 'equipment_defect' && !equipmentId && systemName.trim() === '') {
        setMissing(true);
        return false;
      }
      if (type === 'work_order' && !equipmentId) {
        setMissing(true);
        return false;
      }
      const opened = await source.openFinding?.({
        id: finding.id,
        ...(type === 'equipment_defect'
          ? {
              defect: {
                equipmentId,
                systemName: equipmentId ? null : systemName.trim(),
                severity,
              },
            }
          : {}),
        ...(type === 'work_order' && equipmentId ? { workOrder: { equipmentId, targetOn } } : {}),
      });
      // Uygunsuzluk taslağı HSEQ'de tamamlanır
      if (opened && type === 'sms_nc') {
        shell?.navigate(hsePath('non_conformity', { view: 'detail', id: opened.id, tab: null }));
      }
      return true;
    });
  return (
    <Modal
      opened
      onClose={onClose}
      title={f('title', { type: t(`drillFinding.types.${type}`), no: finding.findingNo })}
      closeButtonProps={{ 'aria-label': t('drillFinding.form.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>{finding.description}</p>
        {type === 'equipment_defect' ? (
          <>
            <p className={classes.hint}>{f('defectHint')}</p>
            <Select
              label={f('equipment')}
              searchable
              clearable
              data={equipment}
              value={equipmentId}
              onChange={setEquipmentId}
            />
            {!equipmentId ? (
              <TextInput
                label={f('system')}
                description={f('noEquipment')}
                maxLength={200}
                value={systemName}
                onChange={(event) => setSystemName(event.currentTarget.value)}
                error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
              />
            ) : null}
            <Select
              label={f('severity')}
              data={(['minor', 'major', 'critical'] as const).map((s) => ({
                value: s,
                label: f(`severities.${s}`),
              }))}
              value={severity}
              allowDeselect={false}
              onChange={(value) => setSeverity((value as typeof severity) ?? 'minor')}
            />
          </>
        ) : null}
        {type === 'work_order' ? (
          <>
            <p className={classes.hint}>{f('workOrderHint')}</p>
            <Select
              label={f('equipment')}
              required
              searchable
              data={equipment}
              value={equipmentId}
              onChange={setEquipmentId}
              error={missing && !equipmentId ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
            />
            <DayInput
              label={f('target')}
              value={targetOn}
              onChange={(value) => setTargetOn(value ?? null)}
            />
          </>
        ) : null}
        {type === 'sms_nc' ? <p className={classes.hint}>{f('ncHint')}</p> : null}
        {type === 'training_need' ? (
          <p className={classes.hint}>{f('trainingHint', { name: finding.personName ?? '—' })}</p>
        ) : null}
        <Failure error={failure} />
        <div className={classes.footerGroup}>
          <Button loading={busy} onClick={() => void confirm()}>
            {f('confirm')}
          </Button>
          <Button variant="default" onClick={onClose}>
            {t('drillFinding.form.close')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

function ReviewDialog({
  finding,
  source,
  onClose,
  onSaved,
}: {
  finding: DrillFinding;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.review.${key}`, values);
  const [assessment, setAssessment] = useState<'adequate' | 'further_action'>('adequate');
  const [comment, setComment] = useState('');
  const [missing, setMissing] = useState(false);
  const { busy, failure, run } = useRun(onSaved);
  return (
    <Modal
      opened
      onClose={onClose}
      title={f('title', { no: finding.findingNo })}
      closeButtonProps={{ 'aria-label': t('drillFinding.form.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>{f('hint')}</p>
        <Radio.Group
          label={f('assessment')}
          value={assessment}
          onChange={(value) => setAssessment(value as typeof assessment)}
        >
          <Stack gap={6} mt={6}>
            <Radio value="adequate" label={f('adequate')} />
            <Radio value="further_action" label={f('further_action')} />
          </Stack>
        </Radio.Group>
        <Textarea
          label={f('comment')}
          required={assessment === 'further_action'}
          autosize
          minRows={2}
          maxLength={2000}
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <Failure error={failure} />
        <div className={classes.footerGroup}>
          <Button
            loading={busy}
            onClick={() =>
              void run(async () => {
                if (assessment === 'further_action' && comment.trim() === '') {
                  setMissing(true);
                  return false;
                }
                await source.reviewFinding?.({
                  id: finding.id,
                  review: { assessment, comment: comment.trim() === '' ? null : comment.trim() },
                });
                return true;
              })
            }
          >
            {f('confirm')}
          </Button>
          <Button variant="default" onClick={onClose}>
            {t('drillFinding.form.close')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}
